import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { ErrorMessage, ErrorMessageTypes } from '@library/common/http';
import { ErrorMessageService } from '@library/core/services';
import { SvgIconsModule } from '@library/common/materials';

interface TypeConfig {
   title: string;
   one: string;
   many: string;
}

export interface ErrorBarItem {
   id: number;
   text: string;
   processId?: string;
   /** Local date and time, e.g. "05/24/2026 03:26 PM". */
   stamp?: string;
   /** Short timezone name, e.g. "EDT". */
   tz?: string;
   /** IANA timezone name, e.g. "America/New_York", used as a tooltip. */
   tzName?: string;
}

export type ErrorBarStamp = Pick<ErrorBarItem, 'stamp' | 'tz' | 'tzName'>;

export interface ErrorBarGroup {
   type: ErrorMessageTypes;
   title: string;
   label: string;
   dismissLabel: string;
   messages: ErrorBarItem[];
}

export interface ErrorBarView {
   type: ErrorMessageTypes;
   headline: string;
   processId?: string;
   stamp?: ErrorBarStamp;
   total: number;
   groups: ErrorBarGroup[];
}

/** Always "mm/dd/yyyy hh:mm AM" regardless of browser locale. */
const STAMP_FORMAT = new Intl.DateTimeFormat( 'en-US', {
   month: '2-digit', day: '2-digit', year: 'numeric',
   hour: '2-digit', minute: '2-digit', hour12: true, timeZoneName: 'short',
});

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * Matches a UUID together with an optional "Process ID" style label in front of it
 * and optional wrapping brackets, e.g. "(Process ID: <uuid>)" or "- processId=<uuid>".
 */
const UUID_WITH_LABEL = new RegExp(
   String.raw`[\s,;:\-–]*[(\[]?\s*(?:(?:lambda|process|request|trace)[\s_-]?id\s*[:=#]?\s*)?` +
   UUID.source +
   String.raw`\s*[)\]]?`,
   'i',
);

/** Ordered by severity, most severe first. */
const TYPES: Record<ErrorMessageTypes, TypeConfig> = {
   error: { title: 'Errors', one: 'error', many: 'errors' },
   alert: { title: 'Alerts', one: 'alert', many: 'alerts' },
   notification: { title: 'Notifications', one: 'notification', many: 'notifications' },
};

/** Icons in shared-assets/icons, keyed by message type. */
const TYPE_ICONS: Record<ErrorMessageTypes, string> = {
   error: 'status_error',
   alert: 'status_alert',
   notification: 'status_info',
};

@Component({
   selector: 'lib-menu-error-bar',
   templateUrl: './error-bar.component.html',
   styleUrls: ['./error-bar.component.scss']
})
export class ErrorBarComponent implements OnInit, OnDestroy {

   @ViewChild( 'list' ) list: ElementRef<HTMLElement>;

   view$: Observable<ErrorBarView | null>;

   readonly typeIcons = TYPE_ICONS;

   expanded = false;

   /** Categories the user has collapsed inside the expanded list. */
   collapsedGroups = new Set<ErrorMessageTypes>();

   copiedId: string | null = null;

   /** Error IDs from the previous update, used to detect newly arrived errors. */
   private seenErrorIds = new Set<number>();

   /** Whether the previous update had more than one message (i.e. the list could be opened). */
   private wasExpandable = false;

   private copiedTimer: ReturnType<typeof setTimeout>;

   _onDestroy: Subject<void> = new Subject();

   constructor(
      public errors: ErrorMessageService,
      private svgIcons: SvgIconsModule,
   ) { }

   ngOnInit() {
      [ ...Object.values( TYPE_ICONS ), 'close', 'chevron_down', 'clock' ].map( icon => this.svgIcons.register( icon ));

      this.view$ = this.errors.messages.pipe(
         takeUntil( this._onDestroy ),
         map( messages => this.buildView( messages )),
      );
   }

   ngOnDestroy() {
      clearTimeout( this.copiedTimer );
      this._onDestroy.next();
      this._onDestroy.complete();
   }

   buildView( messages: ErrorMessage[] ): ErrorBarView | null {
      const groups: ErrorBarGroup[] = ( Object.keys( TYPES ) as ErrorMessageTypes[] )
         .map( type => ({
            type,
            messages: messages
               .filter( m => m.type?.toLowerCase() === type )
               .reverse()
               .map( m => ({ id: m.id, ...this.parseMessage( m.message ), ...this.formatStamp( m.timestamp ) })),
         }))
         .filter( g => g.messages.length )
         .map( g => ({
            ...g,
            title: TYPES[g.type].title,
            label: this.plural( g.messages.length, g.type ),
            dismissLabel: `Dismiss all ${ TYPES[g.type].many }`,
         }));

      const total = groups.reduce(( sum, g ) => sum + g.messages.length, 0 );
      this.updateExpanded( messages, total );
      if ( !total ) {
         return null;
      }

      const top = groups[0];
      let headline = top.messages[0].text;
      if ( total > 1 && top.type !== 'notification' && top.messages.length > 1 ) {
         headline = `${ top.label } need your attention.`;
      }
      const single = total === 1 ? top.messages[0] : undefined;

      return {
         type: top.type,
         headline,
         processId: single?.processId,
         stamp: single?.stamp ? { stamp: single.stamp, tz: single.tz, tzName: single.tzName } : undefined,
         total,
         groups,
      };
   }

   /**
    * Errors open the list by default: expand when a new error arrives, or when the list
    * becomes expandable while errors are present. A manual "Hide" is kept until the next new error.
    */
   private updateExpanded( messages: ErrorMessage[], total: number ) {
      const errorIds = messages.filter( m => m.type?.toLowerCase() === 'error' ).map( m => m.id );
      const hasNewError = errorIds.some( id => !this.seenErrorIds.has( id ));
      const expandable = total > 1;

      if ( !expandable ) {
         this.expanded = false;
      } else if ( errorIds.length && ( hasNewError || !this.wasExpandable )) {
         this.expanded = true;
      }

      if ( hasNewError ) {
         this.collapsedGroups.delete( 'error' );
      }

      this.seenErrorIds = new Set( errorIds );
      this.wasExpandable = expandable;
   }

   /** Formats a timestamp in the user's local timezone. */
   formatStamp( timestamp?: Date | string ): ErrorBarStamp {
      const date = timestamp ? new Date( timestamp ) : null;
      if ( !date || isNaN( date.getTime() )) {
         return {};
      }

      const parts = STAMP_FORMAT.formatToParts( date )
         .reduce(( acc, p ) => ({ ...acc, [p.type]: p.value }), {} as Partial<Record<Intl.DateTimeFormatPartTypes, string>> );

      return {
         stamp: `${ parts.month }/${ parts.day }/${ parts.year } ${ parts.hour }:${ parts.minute } ${ parts.dayPeriod }`,
         tz: parts.timeZoneName,
         tzName: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
   }

   /** Splits a UUID (process ID) out of the message text so it can be shown as fine print. */
   parseMessage( message: string ): Omit<ErrorBarItem, 'id'> {
      const text = message ?? '';
      const processId = text.match( UUID )?.[0];
      if ( !processId ) {
         return { text };
      }

      const stripped = text
         .replace( UUID_WITH_LABEL, ' ' )
         .replace( /\s{2,}/g, ' ' )
         .replace( /\s+([.,;:!?])/g, '$1' )
         .replace( /^[\s,;:\-–]+/, '' )
         .trim();

      return { text: stripped || text, processId };
   }

   copy( processId: string ) {
      navigator.clipboard?.writeText( processId );
      this.copiedId = processId;
      clearTimeout( this.copiedTimer );
      this.copiedTimer = setTimeout(() => this.copiedId = null, 1500 );
   }

   plural( count: number, type: ErrorMessageTypes ): string {
      return `${ count } ${ count === 1 ? TYPES[type].one : TYPES[type].many }`;
   }

   toggle() {
      this.expanded = !this.expanded;
   }

   toggleGroup( type: ErrorMessageTypes ) {
      if ( !this.collapsedGroups.delete( type )) {
         this.collapsedGroups.add( type );
      }
   }

   showGroup( type: ErrorMessageTypes ) {
      this.expanded = true;
      this.collapsedGroups.delete( type );
      setTimeout(() => {
         const container = this.list?.nativeElement;
         const group = container?.querySelector<HTMLElement>( `[data-group="${ type }"]` );
         if ( group ) {
            container.scrollTop = group.offsetTop - container.offsetTop;
         }
      });
   }

   clearAll() {
      this.errors.messages.getValue().map( m => this.errors.clear( m.id ));
   }

   clearType( type: ErrorMessage['type'] ) {
      this.errors.messages.getValue()
         .filter( m => m.type === type )
         .map( m => this.errors.clear( m.id ));
   }

   trackById( _index: number, message: ErrorBarItem ) {
      return message.id;
   }
}
